import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import {
  Upload, Download, Save, RefreshCw, CreditCard, Package, Eye, EyeOff, Image as ImageIcon, MousePointer2,
} from 'lucide-react';
import {
  CR80, DEFAULT_LAYOUT, SIDE_KEYS, sideLayout, mergeLayout, drawBadge, loadBadgeImages, loadImage, renderBadgePng,
} from '../utils/badgeCard';
import { buildZip, saveBlob } from '../utils/zipStore';
import { useTranslation } from 'react-i18next';

const PX = CR80.pxAt(300);

// The studio owns its own height so the card never scrolls out of reach: the
// controls scroll inside their column while the preview stays put. (A sticky
// preview would not work here — the dashboard panel this sits in clips
// overflow, which kills position:sticky.)
const Wrap = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 1.25rem;
  align-items: stretch;
  height: var(--stage-h, calc(100vh - 250px));
  --card-h: clamp(240px, calc(var(--stage-h, 70vh) - 230px), 470px);
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    height: auto;
  }
`;

const Panel = styled.div`
  background: #fff; border: 1px solid #e2e8f0; border-radius: 14px; padding: 1rem 1.125rem;
  h4 { margin: 0 0 .625rem; font-size: .875rem; font-weight: 700; color: #0f172a; display: flex; align-items: center; gap: .4rem; }
  p.hint { margin: 0 0 .75rem; font-size: .78rem; color: #64748b; line-height: 1.5; }
`;

const Stage = styled(Panel)`
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
`;

const Rail = styled.div`
  display: grid;
  gap: 1rem;
  align-content: start;
  min-height: 0;
  overflow-y: auto;
  padding-right: 4px;
  &::-webkit-scrollbar { width: 8px; }
  &::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 8px; }
  &::-webkit-scrollbar-thumb:hover { background: #cbd5e1; }
  @media (max-width: 900px) { overflow: visible; }
`;

// The card height drives everything: the width follows from it so the two
// previews keep the exact CR80 proportion at any viewport, without leaving the
// browser to guess a width from the canvas's intrinsic 638 px. The value comes
// from measuring this box (see the observer in the component) — the panel's
// hint text rewraps at different widths, so any fixed estimate of the chrome
// above eventually clips the captions.
const Cards = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  gap: 1.25rem;
  justify-content: center;
  align-items: center;
  padding: .25rem 0 .5rem;

  figure { margin: 0; display: flex; flex-direction: column; align-items: center; gap: .4rem; }
  figcaption { font-size: .72rem; color: #64748b; font-weight: 600; white-space: nowrap; }
  @media (max-width: 900px) { --card-h: 380px; }
`;

// The drag surface sits exactly on top of the canvas, so a rectangle the
// drawing code reported in card pixels can be placed here as a percentage and
// land on the pixels it describes at any preview size.
const Surface = styled.div`
  position: relative;
  height: var(--card-h);
  width: calc(var(--card-h) * ${PX.width} / ${PX.height});
  touch-action: none;
  outline: none;
  cursor: ${(p) => (p.$over ? 'grab' : 'default')};
  &.dragging { cursor: grabbing; }

  canvas {
    display: block; width: 100%; height: 100%;
    border-radius: 10px; background: #fff;
    box-shadow: 0 8px 22px rgba(15, 23, 42, .16);
  }
  &:focus-visible canvas { box-shadow: 0 0 0 2px #2563eb, 0 8px 22px rgba(15, 23, 42, .16); }
`;

const Marquee = styled.div`
  position: absolute;
  border: 1.5px solid #2563eb;
  border-radius: 4px;
  background: rgba(37, 99, 235, .07);
  pointer-events: none;

  .tag {
    position: absolute; left: -1.5px; bottom: 100%; margin-bottom: 3px;
    background: #2563eb; color: #fff; font-size: 10px; font-weight: 700;
    padding: 1px 5px; border-radius: 4px; white-space: nowrap;
  }
  .handle {
    position: absolute; right: -6px; bottom: -6px; width: 12px; height: 12px;
    background: #fff; border: 1.5px solid #2563eb; border-radius: 3px;
    pointer-events: auto; cursor: nwse-resize;
  }
`;

const Guide = styled.div`
  position: absolute; background: #f43f5e; pointer-events: none;
  &.v { top: 0; bottom: 0; width: 1px; left: 50%; }
  &.h { left: 0; right: 0; height: 1px; top: 50%; }
`;

const Row = styled.div`
  display: grid; grid-template-columns: 58px 1fr 40px; gap: .4rem;
  align-items: center; margin-bottom: .3rem;
  label { font-size: .72rem; color: #475569; font-weight: 600; }
  input[type=range] { width: 100%; accent-color: #2563eb; }
  .val { font-size: .7rem; color: #64748b; text-align: right; font-variant-numeric: tabular-nums; }
`;

const Btn = styled.button`
  font-family: inherit; font-size: .8rem; font-weight: 600; border-radius: 9px;
  padding: .5rem .8rem; cursor: pointer; display: inline-flex; align-items: center; gap: .35rem;
  background: ${(p) => (p.$primary ? '#2563eb' : '#fff')};
  color: ${(p) => (p.$primary ? '#fff' : '#334155')};
  border: 1px solid ${(p) => (p.$primary ? '#2563eb' : '#e2e8f0')};
  &:disabled { opacity: .55; cursor: not-allowed; }
`;

const Bar = styled.div`display:flex; gap:.5rem; flex-wrap:wrap; align-items:center; margin-top:.875rem;`;

const Note = styled.div`
  margin-top: .75rem; border-radius: 9px; padding: .55rem .7rem; font-size: .78rem;
  background: ${(p) => (p.$kind === 'error' ? '#fef2f2' : '#f0fdf4')};
  color: ${(p) => (p.$kind === 'error' ? '#991b1b' : '#166534')};
  border: 1px solid ${(p) => (p.$kind === 'error' ? '#fecaca' : '#bbf7d0')};
`;

const Select = styled.select`
  width: 100%; font-family: inherit; font-size: .8rem; padding: .45rem .5rem;
  border: 1px solid #e2e8f0; border-radius: 9px; color: #0f172a; background: #fff;
`;

const TextInput = styled.input`
  width: 100%; font-family: inherit; font-size: .76rem; padding: .4rem .5rem;
  border: 1px solid #e2e8f0; border-radius: 8px; color: #0f172a; margin-bottom: .35rem;
`;

const TextArea = styled.textarea`
  width: 100%; font-family: inherit; font-size: .74rem; line-height: 1.5; padding: .45rem .5rem;
  border: 1px solid #e2e8f0; border-radius: 8px; color: #0f172a; resize: vertical; min-height: 132px;
  margin-bottom: .35rem;
`;

const Segmented = styled.div`
  display: grid; grid-auto-flow: column; grid-auto-columns: 1fr; gap: 4px;
  background: #f1f5f9; border-radius: 9px; padding: 3px; margin-bottom: .6rem;
  button {
    font-family: inherit; font-size: .72rem; font-weight: 600; padding: .3rem 0;
    border: 0; border-radius: 7px; cursor: pointer;
    background: transparent; color: #64748b;
    &.on { background: #fff; color: #1d4ed8; box-shadow: 0 1px 2px rgba(15,23,42,.12); }
  }
`;

const SideLabel = styled.div`
  font-size: .68rem; font-weight: 800; letter-spacing: .06em; text-transform: uppercase;
  color: #94a3b8; margin: .5rem 0 .3rem;
  &:first-of-type { margin-top: 0; }
`;

const ElementRow = styled.div`
  border: 1px solid ${(p) => (p.$active ? '#bfdbfe' : 'transparent')};
  background: ${(p) => (p.$active ? '#f8fbff' : 'transparent')};
  border-radius: 10px;
  padding: ${(p) => (p.$active ? '.45rem .55rem .3rem' : '.2rem .55rem')};
  margin-bottom: .15rem;

  .head {
    display: flex; align-items: center; gap: .45rem; cursor: pointer;
    font-size: .78rem; font-weight: 600; color: ${(p) => (p.$active ? '#1d4ed8' : '#334155')};
    padding: .25rem 0;
  }
  .head .name { flex: 1; }
  .head button {
    border: 0; background: none; padding: 2px; cursor: pointer; color: #94a3b8; display: flex;
    &:hover { color: #475569; }
  }
  .off { opacity: .45; }
  .sub { font-size: .7rem; color: #64748b; font-weight: 600; margin: .35rem 0 .15rem; }
`;

// Everything that can be placed on a card. Both sides draw from the same
// vocabulary — which keys a side actually owns comes from SIDE_KEYS.
const META = {
  logo: { label: 'Logo', sizeMax: 0.9 },
  photo: { label: 'Photo', sizeMax: 0.95 },
  name: { label: 'Name', sizeMax: 0.16 },
  role: { label: 'Role', sizeMax: 0.10 },
  id: { label: 'ID number', sizeMax: 0.09 },
  phone: { label: 'Phone row', sizeMax: 0.09 },
  qr: { label: 'QR code', sizeMax: 0.55 },
  terms: { label: 'Terms & conditions', sizeMax: 0.09 },
};
const SIDES = ['front', 'back'];
const SIZE_MIN = 0.008;
const SNAP = 0.012;          // how close to a centre line before it grabs
const GRAB_PAD = PX.width * 0.015;   // thin text rows still need something to hold

const clamp01 = (n) => Math.min(1, Math.max(0, n));
const pct = (n) => `${n * 100}%`;

const token = () => localStorage.getItem('token');
const authHeader = () => ({ Authorization: `Bearer ${token()}` });

/**
 * Design and export employee cards on the store's own artwork.
 *
 * Both sides are editable and both are dragged straight on the preview; the
 * sliders are the fine-tune, not the only way in. The preview and the exported
 * PNG are drawn by the same function at different sizes, so what is on screen
 * is what lands in the file. Export is 300 dpi CR80 portrait — the stock an
 * Evolis Primacy 2 takes.
 */
const BadgeStudio = () => {
  const { t } = useTranslation();
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState('');
  const [staff, setStaff] = useState('store');   // 'store' | 'unassigned' (lab, technicians, admins)
  const [data, setData] = useState(null);
  const [layout, setLayout] = useState(mergeLayout(null));
  const [selectedId, setSelectedId] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [progress, setProgress] = useState('');

  const [images, setImages] = useState(null);      // decoded once per employee, not per drag frame
  const [boxes, setBoxes] = useState({ front: {}, back: {} });   // where each element landed, in card px
  const [selected, setSelected] = useState({ side: 'front', key: 'photo' });
  const [hovering, setHovering] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [guides, setGuides] = useState({});
  const [stageH, setStageH] = useState(null);
  const [cardH, setCardH] = useState(null);

  const wrapRef = useRef(null);
  const cardsRef = useRef(null);
  const canvasRefs = { front: useRef(null), back: useRef(null) };
  const surfaceRefs = { front: useRef(null), back: useRef(null) };
  const frontFile = useRef(null);
  const backFile = useRef(null);
  const logoFile = useRef(null);
  const dragRef = useRef(null);

  // Fit the studio to whatever room the dashboard left it, so the page itself
  // never scrolls and the card cannot be scrolled out of sight while the
  // controls are being used.
  useEffect(() => {
    const fit = () => {
      const top = wrapRef.current?.getBoundingClientRect().top;
      if (top == null) return;
      setStageH(Math.max(520, Math.round(window.innerHeight - top - 28)));
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, []);

  // …and fit the cards to whatever is left inside that, caption included. The
  // cards row takes its height from the flex leftover, not from the cards, so
  // writing the measurement back cannot feed into itself.
  useEffect(() => {
    const el = cardsRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const CAPTION = 36;   // caption line + its gap + the row's own padding
    const observer = new ResizeObserver(() => {
      setCardH(Math.max(220, Math.round(el.clientHeight - CAPTION)));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    axios.get('/api/stores', { headers: authHeader() })
      .then((r) => {
        const list = r.data?.stores || r.data || [];
        setStores(list);
        if (list.length && !storeId) setStoreId(list[0]._id || list[0].id);
      })
      .catch(() => setNotice({ kind: 'error', message: 'Could not load stores.' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    if (!storeId) return;
    setBusy(true);
    setNotice(null);
    try {
      const staffParam = staff === 'unassigned' ? '&staff=unassigned' : '';
      const { data: d } = await axios.get(`/api/users/badge-data?storeId=${storeId}${staffParam}`, { headers: authHeader() });
      setData(d);
      setLayout(mergeLayout(d.layout));
      setSelectedId(d.employees?.[0]?.id || '');
      if (!d.employees?.length) {
        setNotice({ kind: 'error', message: staff === 'unassigned' ? 'No active employees without a store.' : 'No active employees at this store.' });
      }
    } catch (err) {
      setNotice({ kind: 'error', message: err.response?.data?.message || 'Could not load badge data.' });
    } finally {
      setBusy(false);
    }
  }, [storeId, staff]);

  useEffect(() => { load(); }, [load]);

  const employee = useMemo(
    () => (data?.employees || []).find((e) => e.id === selectedId) || data?.employees?.[0] || null,
    [data, selectedId],
  );

  // Decode the artwork, portrait, QR and logo once per employee. Doing this in
  // the draw pass would re-decode a few hundred KB of data URIs on every
  // pointer move and make dragging crawl.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!employee) { setImages(null); return; }
      const [front, backTemplate] = await Promise.all([
        loadBadgeImages({ employee, template: data?.template?.front, logo: data?.template?.logo }),
        loadImage(data?.template?.back),
      ]);
      // The back carries the logo and the ID too, so it needs the same images —
      // only the artwork differs.
      if (!cancelled) setImages({ front, back: { ...front, template: backTemplate } });
    })();
    return () => { cancelled = true; };
  }, [employee, data]);

  // Redraw — synchronous, so a drag repaints in the same frame.
  useEffect(() => {
    if (!images || !employee) return;
    const drawn = {};
    for (const side of SIDES) {
      const canvas = canvasRefs[side].current;
      if (!canvas) continue;
      canvas.width = PX.width;
      canvas.height = PX.height;
      drawn[side] = drawBadge(canvas.getContext('2d'), {
        width: PX.width, height: PX.height, images: images[side], employee, layout, side,
      });
    }
    setBoxes(drawn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images, employee, layout]);

  // ── Layout access, per side ────────────────────────────────────────────────

  const cfgOf = useCallback(
    (side, key) => sideLayout(layout, side)[key],
    [layout],
  );

  const set = useCallback((side, key, field, value) => setLayout((prev) => (side === 'back'
    ? { ...prev, back: { ...prev.back, [key]: { ...prev.back[key], [field]: value } } }
    : { ...prev, [key]: { ...prev[key], [field]: value } })), []);

  // ── Direct manipulation ────────────────────────────────────────────────────

  const pointAt = (e, side) => {
    const r = surfaceRefs[side].current.getBoundingClientRect();
    return { fx: (e.clientX - r.left) / r.width, fy: (e.clientY - r.top) / r.height };
  };

  // Smallest box wins, so a name sitting on top of the photo is still grabbable.
  const hitTest = useCallback((side, fx, fy) => {
    const x = fx * PX.width;
    const y = fy * PX.height;
    let best = null;
    for (const key of SIDE_KEYS[side]) {
      const b = boxes[side]?.[key];
      if (!b || !sideLayout(layout, side)[key]?.show) continue;
      if (x < b.x - GRAB_PAD || x > b.x + b.w + GRAB_PAD) continue;
      if (y < b.y - GRAB_PAD || y > b.y + b.h + GRAB_PAD) continue;
      const area = b.w * b.h;
      if (!best || area < best.area) best = { key, area };
    }
    return best?.key || null;
  }, [boxes, layout]);

  const startResize = (e, side, key) => {
    e.preventDefault();
    e.stopPropagation();
    const { fx, fy } = pointAt(e, side);
    const cfg = cfgOf(side, key);
    const dist = Math.hypot((fx - cfg.x) * PX.width, (fy - cfg.y) * PX.height);
    surfaceRefs[side].current.setPointerCapture(e.pointerId);
    dragRef.current = { mode: 'resize', side, key, dist: Math.max(dist, 1), size: cfg.size };
    setDragging(true);
  };

  const onPointerDown = (e, side) => {
    if (!employee) return;
    const { fx, fy } = pointAt(e, side);
    const key = hitTest(side, fx, fy);
    setSelected(key ? { side, key } : { side, key: null });
    surfaceRefs[side].current.focus();
    if (!key) return;
    e.preventDefault();
    surfaceRefs[side].current.setPointerCapture(e.pointerId);
    const cfg = cfgOf(side, key);
    dragRef.current = { mode: 'move', side, key, dx: cfg.x - fx, dy: cfg.y - fy };
    setDragging(true);
  };

  const onPointerMove = (e, side) => {
    const drag = dragRef.current;
    const { fx, fy } = pointAt(e, side);
    if (!drag) {
      setHovering(Boolean(hitTest(side, fx, fy)));
      return;
    }
    if (drag.mode === 'move') {
      let x = clamp01(fx + drag.dx);
      let y = clamp01(fy + drag.dy);
      const g = {};
      // Centre lines snap unless Alt is held — most cards are centred, and
      // eyeballing 50% on a 260px preview is a lottery.
      if (!e.altKey) {
        if (Math.abs(x - 0.5) < SNAP) { x = 0.5; g.v = true; }
        if (Math.abs(y - 0.5) < SNAP) { y = 0.5; g.h = true; }
      }
      setGuides({ ...g, side: drag.side });
      set(drag.side, drag.key, 'x', x);
      set(drag.side, drag.key, 'y', y);
    } else {
      const cfg = cfgOf(drag.side, drag.key);
      const dist = Math.hypot((fx - cfg.x) * PX.width, (fy - cfg.y) * PX.height);
      const max = META[drag.key]?.sizeMax || 1;
      set(drag.side, drag.key, 'size',
        Math.min(max, Math.max(SIZE_MIN, (drag.size * dist) / drag.dist)));
    }
  };

  const endDrag = (e, side) => {
    if (!dragRef.current) return;
    try { surfaceRefs[side].current.releasePointerCapture(e.pointerId); } catch { /* already gone */ }
    dragRef.current = null;
    setDragging(false);
    setGuides({});
  };

  const onKeyDown = (e, side) => {
    const key = selected.side === side ? selected.key : null;
    if (!key) return;
    const cfg = cfgOf(side, key);
    const step = e.shiftKey ? 0.02 : 0.004;
    const moves = {
      ArrowLeft: ['x', -step], ArrowRight: ['x', step], ArrowUp: ['y', -step], ArrowDown: ['y', step],
    };
    if (moves[e.key]) {
      e.preventDefault();
      const [field, delta] = moves[e.key];
      set(side, key, field, clamp01(cfg[field] + delta));
      return;
    }
    if (e.key === '+' || e.key === '=' || e.key === '-') {
      e.preventDefault();
      const max = META[key]?.sizeMax || 1;
      const factor = e.key === '-' ? 0.95 : 1.05;
      set(side, key, 'size', Math.min(max, Math.max(SIZE_MIN, cfg.size * factor)));
      return;
    }
    if (e.key === 'Escape') setSelected({ side, key: null });
  };

  const marqueeFor = (side) => {
    if (selected.side !== side || !selected.key) return null;
    const cfg = cfgOf(side, selected.key);
    const box = boxes[side]?.[selected.key];
    return cfg?.show && box ? box : null;
  };

  // ── Server round-trips ─────────────────────────────────────────────────────

  const uploadTemplates = async () => {
    const front = frontFile.current?.files?.[0];
    const back = backFile.current?.files?.[0];
    const logo = logoFile.current?.files?.[0];
    if (!front && !back && !logo) { setNotice({ kind: 'error', message: 'Choose a front, back or logo image first.' }); return; }
    setBusy(true);
    try {
      const form = new FormData();
      if (front) form.append('front', front);
      if (back) form.append('back', back);
      if (logo) form.append('logo', logo);
      await axios.post(`/api/stores/${storeId}/badge-template`, form, {
        headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' },
      });
      [frontFile, backFile, logoFile].forEach((r) => { if (r.current) r.current.value = ''; });
      setNotice({ kind: 'success', message: 'Template saved.' });
      await load();
      // load() replaces the layout with what the store has saved, so turning the
      // new logo on has to happen after it — otherwise the upload appears to do
      // nothing.
      if (logo) { set('front', 'logo', 'show', true); setSelected({ side: 'front', key: 'logo' }); }
    } catch (err) {
      setNotice({ kind: 'error', message: err.response?.data?.message || 'Could not save the template.' });
    } finally {
      setBusy(false);
    }
  };

  const saveLayout = async () => {
    setBusy(true);
    try {
      await axios.put(`/api/stores/${storeId}/badge-layout`, { layout }, { headers: authHeader() });
      setNotice({ kind: 'success', message: 'Layout and terms saved for this store.' });
    } catch (err) {
      setNotice({ kind: 'error', message: err.response?.data?.message || 'Could not save the layout.' });
    } finally {
      setBusy(false);
    }
  };

  const safeName = (e) => `${(e.employeeId || e.id).replace(/[^\w-]/g, '')}-${(e.name || '').replace(/[^\w]+/g, '_')}`;

  // The back used to be nothing but artwork, so it was skipped when no artwork
  // was uploaded. It can now carry the logo, terms and ID on its own.
  const backHasContent = Boolean(data?.template?.back)
    || SIDE_KEYS.back.some((key) => layout.back?.[key]?.show);

  const downloadOne = async (side) => {
    if (!employee) return;
    setBusy(true);
    try {
      const blob = await renderBadgePng({
        employee, template: data?.template?.[side], logo: data?.template?.logo, layout, side, dpi: 300,
      });
      saveBlob(blob, `${safeName(employee)}-${side}.png`);
    } finally {
      setBusy(false);
    }
  };

  const downloadAll = async () => {
    const list = data?.employees || [];
    if (!list.length) return;
    setBusy(true);
    const files = [];
    try {
      for (let i = 0; i < list.length; i += 1) {
        const e = list[i];
        setProgress(`Rendering ${i + 1} of ${list.length}…`);
        for (const side of SIDES) {
          if (side === 'back' && !backHasContent) continue;
          // eslint-disable-next-line no-await-in-loop
          const blob = await renderBadgePng({
            employee: e, template: data?.template?.[side], logo: data?.template?.logo, layout, side, dpi: 300,
          });
          // eslint-disable-next-line no-await-in-loop
          files.push({ name: `${safeName(e)}-${side}.png`, data: await blob.arrayBuffer() });
        }
      }
      setProgress('Packing…');
      const zip = await buildZip(files);
      saveBlob(zip, `badges-${(data?.store?.name || 'store').replace(/[^\w]+/g, '_')}.zip`);
      setNotice({ kind: 'success', message: `${files.length} card(s) exported at 300 dpi, CR80 portrait.` });
    } catch (err) {
      setNotice({ kind: 'error', message: err.message || 'Export failed.' });
    } finally {
      setProgress('');
      setBusy(false);
    }
  };

  // ── Rail ───────────────────────────────────────────────────────────────────

  const renderElementRow = (side, key) => {
    const cfg = cfgOf(side, key);
    if (!cfg) return null;
    const { label, sizeMax } = META[key];
    const active = selected.side === side && selected.key === key;
    const noImage = key === 'logo' && !data?.template?.logo;
    return (
      <ElementRow key={`${side}-${key}`} $active={active}>
        <div
          className={`head${cfg.show ? '' : ' off'}`}
          onClick={() => setSelected({ side, key })}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter') setSelected({ side, key }); }}
        >
          <span className="name">{label}{noImage ? t('badgeStudio.noImage') : ''}</span>
          <button
            type="button"
            title={cfg.show ? 'Hide on card' : 'Show on card'}
            onClick={(e) => { e.stopPropagation(); set(side, key, 'show', !cfg.show); }}
          >
            {cfg.show ? <Eye size={14} /> : <EyeOff size={14} />}
          </button>
        </div>
        {active && cfg.show && (
          <>
            {key === 'terms' && (
              <>
                <div className="sub">{t('badgeStudio.heading')}</div>
                <TextInput
                  value={cfg.title || ''}
                  placeholder={t('badgeStudio.termsConditions')}
                  onChange={(e) => set(side, key, 'title', e.target.value)}
                />
                <div className="sub">{t('badgeStudio.textAParagraphStartingSomething')}</div>
                <TextArea
                  value={cfg.text || ''}
                  onChange={(e) => set(side, key, 'text', e.target.value)}
                />
                <Segmented>
                  {[['left', 'Left'], ['center', 'Centred']].map(([value, l]) => (
                    <button
                      key={value}
                      type="button"
                      className={(cfg.align || 'left') === value ? 'on' : ''}
                      onClick={() => set(side, key, 'align', value)}
                    >
                      {l}
                    </button>
                  ))}
                </Segmented>
                <Row>
                  <label>{t('badgeStudio.width')}</label>
                  <input type="range" min="0.3" max="0.96" step="0.01" value={cfg.width}
                    onChange={(e) => set(side, key, 'width', Number(e.target.value))} />
                  <span className="val">{Math.round(cfg.width * 100)}%</span>
                </Row>
                <Row>
                  <label>{t('badgeStudio.leading')}</label>
                  <input type="range" min="1.1" max="2.2" step="0.05" value={cfg.lineHeight}
                    onChange={(e) => set(side, key, 'lineHeight', Number(e.target.value))} />
                  <span className="val">{cfg.lineHeight}</span>
                </Row>
              </>
            )}
            {key === 'id' && (
              <>
                <div className="sub">{t('badgeStudio.label')}</div>
                <TextInput
                  value={cfg.label || ''}
                  placeholder={t('badgeStudio.noId')}
                  onChange={(e) => set(side, key, 'label', e.target.value)}
                />
              </>
            )}
            <Row>
              <label>{t('badgeStudio.across')}</label>
              <input type="range" min="0" max="1" step="0.005" value={cfg.x}
                onChange={(e) => set(side, key, 'x', Number(e.target.value))} />
              <span className="val">{Math.round(cfg.x * 100)}%</span>
            </Row>
            <Row>
              <label>{key === 'terms' ? t('badgeStudio.top') : t('badgeStudio.down')}</label>
              <input type="range" min="0" max="1" step="0.005" value={cfg.y}
                onChange={(e) => set(side, key, 'y', Number(e.target.value))} />
              <span className="val">{Math.round(cfg.y * 100)}%</span>
            </Row>
            <Row>
              <label>{t('badgeStudio.size')}</label>
              <input type="range" min={SIZE_MIN} max={sizeMax} step="0.002" value={cfg.size}
                onChange={(e) => set(side, key, 'size', Number(e.target.value))} />
              <span className="val">{Math.round(cfg.size * 1000) / 10}</span>
            </Row>
          </>
        )}
      </ElementRow>
    );
  };

  const renderSurface = (side, caption) => {
    const box = marqueeFor(side);
    return (
      <figure>
        <Surface
          ref={surfaceRefs[side]}
          className={dragging && dragRef.current?.side === side ? 'dragging' : ''}
          $over={hovering}
          tabIndex={0}
          onPointerDown={(e) => onPointerDown(e, side)}
          onPointerMove={(e) => onPointerMove(e, side)}
          onPointerUp={(e) => endDrag(e, side)}
          onPointerCancel={(e) => endDrag(e, side)}
          onKeyDown={(e) => onKeyDown(e, side)}
        >
          <canvas ref={canvasRefs[side]} />
          {box && (
            <Marquee
              style={{
                left: pct(box.x / PX.width),
                top: pct(box.y / PX.height),
                width: pct(box.w / PX.width),
                height: pct(box.h / PX.height),
              }}
            >
              <span className="tag">{META[selected.key]?.label}</span>
              <span className="handle" onPointerDown={(e) => startResize(e, side, selected.key)} />
            </Marquee>
          )}
          {guides.side === side && guides.v && <Guide className="v" />}
          {guides.side === side && guides.h && <Guide className="h" />}
        </Surface>
        <figcaption>{caption}</figcaption>
      </figure>
    );
  };

  return (
    <Wrap
      ref={wrapRef}
      style={{
        ...(stageH ? { '--stage-h': `${stageH}px` } : null),
        ...(cardH ? { '--card-h': `${cardH}px` } : null),
      }}
    >
      <Stage>
        <h4><CreditCard size={15} /> {t('badgeStudio.cardPreview')} {employee?.name || 'no employee'}</h4>
        <p className="hint" style={{ display: 'flex', alignItems: 'center', gap: '.35rem' }}>
          <MousePointer2 size={13} />
          {t('badgeStudio.dragAnythingOnEitherSide')}{' '}
          {PX.width}×{PX.height} {t('badgeStudio.pxCr80PortraitAt300')}
        </p>

        <Cards ref={cardsRef}>
          {renderSurface('front', 'Front — drag to place')}
          {renderSurface('back', `Back — drag to place${data?.template?.back ? '' : ' (no artwork)'}`)}
        </Cards>

        <Bar style={{ marginTop: 0 }}>
          <Select value={staff} onChange={(e) => setStaff(e.target.value)} style={{ width: 190 }}>
            <option value="store">This store's staff</option>
            <option value="unassigned">Lab, technicians &amp; admins</option>
          </Select>
          <Select value={selectedId} onChange={(e) => setSelectedId(e.target.value)} style={{ width: 200 }}>
            {(data?.employees || []).map((e) => (
              <option key={e.id} value={e.id}>{e.name} — {e.employeeId}</option>
            ))}
          </Select>
          <Btn onClick={() => downloadOne('front')} disabled={busy || !employee}><Download size={14} /> {t('badgeStudio.frontPng')}</Btn>
          {backHasContent && (
            <Btn onClick={() => downloadOne('back')} disabled={busy || !employee}><Download size={14} /> {t('badgeStudio.backPng')}</Btn>
          )}
          <Btn $primary onClick={downloadAll} disabled={busy || !(data?.employees || []).length}>
            <Package size={14} /> {progress || `Download all (${(data?.employees || []).length})`}
          </Btn>
        </Bar>
        {notice && <Note $kind={notice.kind}>{notice.message}</Note>}
      </Stage>

      <Rail>
        <Panel>
          <h4><Upload size={15} /> {t('badgeStudio.yourTemplate')}</h4>
          <p className="hint">
            {t('badgeStudio.uploadTheCardArtworkAs')}
          </p>
          <Select value={storeId} onChange={(e) => setStoreId(e.target.value)} style={{ marginBottom: '.6rem' }}>
            {stores.map((s) => <option key={s._id || s.id} value={s._id || s.id}>{s.name}</option>)}
          </Select>
          <div style={{ fontSize: '.72rem', color: '#475569', fontWeight: 600, marginBottom: 2 }}>{t('badgeStudio.front')}</div>
          <input ref={frontFile} type="file" accept="image/*" style={{ fontSize: '.72rem', marginBottom: '.5rem', width: '100%' }} />
          <div style={{ fontSize: '.72rem', color: '#475569', fontWeight: 600, marginBottom: 2 }}>{t('badgeStudio.back')}</div>
          <input ref={backFile} type="file" accept="image/*" style={{ fontSize: '.72rem', marginBottom: '.5rem', width: '100%' }} />
          <div style={{ fontSize: '.72rem', color: '#475569', fontWeight: 600, marginBottom: 4 }}>{t('badgeStudio.artworkOnTheCard')}</div>
          <Segmented>
            {[['cover', 'Fill'], ['contain', 'Fit'], ['stretch', 'Stretch']].map(([value, label]) => (
              <button
                key={value}
                type="button"
                className={(layout.artwork?.fit || 'cover') === value ? 'on' : ''}
                onClick={() => setLayout((prev) => ({ ...prev, artwork: { ...prev.artwork, fit: value } }))}
                title={{
                  cover: 'Fills the card, crops whatever does not fit',
                  contain: 'Shows the whole design, white where the proportions differ',
                  stretch: 'Stretches the design to the card, distorting it',
                }[value]}
              >
                {label}
              </button>
            ))}
          </Segmented>
          <div style={{ fontSize: '.72rem', color: '#475569', fontWeight: 600, marginBottom: 2, display: 'flex', alignItems: 'center', gap: '.3rem' }}>
            <ImageIcon size={12} /> {t('badgeStudio.logoPngWithTransparency')}
          </div>
          <input ref={logoFile} type="file" accept="image/*" style={{ fontSize: '.72rem', marginBottom: '.35rem', width: '100%' }} />
          <div style={{ fontSize: '.7rem', color: '#94a3b8', marginBottom: '.6rem' }}>
            {data?.template?.logoSource === 'badge' && 'Using the badge logo you uploaded.'}
            {data?.template?.logoSource === 'store' && 'No badge logo yet — using this store’s logo.'}
            {!data?.template?.logoSource && 'No logo available for this store yet.'}
          </div>
          <Bar style={{ marginTop: 0 }}>
            <Btn $primary onClick={uploadTemplates} disabled={busy}><Upload size={14} /> {t('badgeStudio.saveTemplate')}</Btn>
            <Btn onClick={load} disabled={busy}><RefreshCw size={14} /> {t('badgeStudio.reload')}</Btn>
          </Bar>
        </Panel>

        <Panel>
          <h4>{t('badgeStudio.elements')}</h4>
          <p className="hint">
            {t('badgeStudio.clickOneToSelectIt')}
          </p>
          <SideLabel>{t('badgeStudio.frontOfCard')}</SideLabel>
          {SIDE_KEYS.front.map((key) => renderElementRow('front', key))}
          <SideLabel>{t('badgeStudio.backOfCard')}</SideLabel>
          {SIDE_KEYS.back.map((key) => renderElementRow('back', key))}
          <Bar style={{ marginTop: '.5rem' }}>
            <Btn $primary onClick={saveLayout} disabled={busy}><Save size={14} /> {t('badgeStudio.saveLayout')}</Btn>
            <Btn onClick={() => setLayout(mergeLayout(DEFAULT_LAYOUT))} disabled={busy}>{t('badgeStudio.reset')}</Btn>
          </Bar>
        </Panel>
      </Rail>
    </Wrap>
  );
};

export default BadgeStudio;
